import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import axios from '../api/axios';
import { toast } from '../components/Toast';
import { useAuth } from '../context/AuthContext';
import './Settings.css';

function CompanySettings() {
  const { isCompanyAdmin, setCompanyCountry, hasMenuAccess } = useAuth();
  const [profile, setProfile] = useState({
    name: '',
    country: '',
    industry: '',
    phone: '',
    alternatePhone: '',
    careOf: '',
    secondContactName: '',
    secondContact: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/company/profile');
      const data = res.data?.data || res.data;
      if (data) {
        setProfile({
          name: data.name || '',
          country: data.country || '',
          industry: data.industry || '',
          phone: data.phone || '',
          alternatePhone: data.alternatePhone || '',
          careOf: data.careOf || '',
          secondContactName: data.secondContactName || '',
          secondContact: data.secondContact || ''
        });
      }
    } catch (err) {
      toast.error('Failed to load company profile');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfile(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put('/company/profile', profile);
      toast.success('Company profile updated successfully');
      sessionStorage.setItem('companyCountry', profile.country);
      setCompanyCountry(profile.country);
    } catch (err) {
      toast.error('Failed to update company profile');
    } finally {
      setSaving(false);
    }
  };

  // Redirect non-admins
  if (!isCompanyAdmin() && !hasMenuAccess('company-settings')) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="settings-container">
      <div className="settings-header">
        <h1>Company Details</h1>
        <p>Manage company profile and currency settings</p>
      </div>

      <div className="settings-card">
        <div className="settings-tab-content">
          {loading ? (
            <div className="settings-loading">
              <div className="settings-loading-spinner" />
              <p>Loading profile…</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="settings-note">
                <span className="settings-note-icon">🏢</span>
                <span>
                  Update your company's profile information. Selecting the correct Country will automatically format currencies and displays the appropriate symbols (e.g. India displays ₹, United States/others display $).
                </span>
              </div>

              <div className="settings-form-grid">
                <div className="form-group">
                  <label>Company Name <span className="required-star">*</span></label>
                  <input
                    name="name"
                    type="text"
                    required
                    value={profile.name}
                    onChange={handleChange}
                    disabled={saving}
                  />
                </div>

                <div className="form-group">
                  <label>Country <span className="required-star">*</span></label>
                  <select
                    name="country"
                    value={profile.country}
                    onChange={handleChange}
                    required
                    disabled={saving}
                    className="select-input"
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd' }}
                  >
                    <option value="">Select Country</option>
                    <option value="India">India</option>
                    <option value="United States">United States</option>
                    <option value="United Kingdom">United Kingdom</option>
                    <option value="Canada">Canada</option>
                    <option value="Australia">Australia</option>
                    <option value="Singapore">Singapore</option>
                    <option value="United Arab Emirates">United Arab Emirates</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Industry</label>
                  <input
                    name="industry"
                    type="text"
                    value={profile.industry}
                    onChange={handleChange}
                    disabled={saving}
                  />
                </div>

                <div className="form-group">
                  <label>Phone</label>
                  <input
                    name="phone"
                    type="text"
                    value={profile.phone}
                    onChange={handleChange}
                    disabled={saving}
                  />
                </div>

                <div className="form-group">
                  <label>Alternate Phone</label>
                  <input
                    name="alternatePhone"
                    type="text"
                    value={profile.alternatePhone}
                    onChange={handleChange}
                    disabled={saving}
                  />
                </div>

                <div className="form-group">
                  <label>Care Of</label>
                  <input
                    name="careOf"
                    type="text"
                    value={profile.careOf}
                    onChange={handleChange}
                    disabled={saving}
                  />
                </div>

                <div className="form-group">
                  <label>Second Contact Name</label>
                  <input
                    name="secondContactName"
                    type="text"
                    value={profile.secondContactName}
                    onChange={handleChange}
                    disabled={saving}
                  />
                </div>

                <div className="form-group">
                  <label>Second Contact Phone</label>
                  <input
                    name="secondContact"
                    type="text"
                    value={profile.secondContact}
                    onChange={handleChange}
                    disabled={saving}
                  />
                </div>
              </div>

              <div className="settings-form-actions">
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? '💾 Saving…' : '💾 Save Profile'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default CompanySettings;
